import React, { useEffect, useState } from "react";
import { ReactComponent as PlusIcon } from "../../../Assets/ActionIcons/PlusIcon.svg";
import { ReactComponent as NoAvailablePaymentMethod } from "../../../Assets/CustomerDetailPage/NoAvailablePaymentMethod.svg";
import { AdyenPaymentMethodType, getPaymentMethodDetails } from "../../../Contracts/Transaction";
import { usePaymentMethodManagementStore } from "../../../Store/PaymentMethodManagement";
import { ReactComponent as InfoIcon } from "../../../Assets/ActionIcons/InfoIcon.svg";
import PaymentForm from "../../../Components/Organisms/PaymentForm";
import { AdyenMandatedBy } from "../../../Contracts/AdyenSession";
import Modal from "../../../Components/Atoms/Modal";
import Popover from "../../../Components/Atoms/Popover";

export interface ClientPortalCustomerManagePaymentMethodsState {
  customerId: string;
}

const ClientPortalCustomerManagePaymentMethods: React.FC<
  ClientPortalCustomerManagePaymentMethodsState
> = ({ customerId }) => {
  const [addNewCardActive, setAddNewCardActive] = useState<boolean>(false);
  const [paymentMethodAddedSuccessDialogueVisible, setPaymentMethodAddedSuccessDialogueVisible] =
    useState<{ method: AdyenPaymentMethodType | null; open: boolean }>({
      method: null,
      open: false
    });

  const { allowedPaymentMethods, availablePaymentMethods, paymentsAdyenSessionData } =
    usePaymentMethodManagementStore();
  const {
    getAllowedPaymentMethods,
    getAvailablePaymentMethods,
    createAddPaymentMethodMandate,
    createTransactionToken,
    handleBacsPayment,
    sendMandateCreationNotification,
    doGiactValidation
  } = usePaymentMethodManagementStore().clientPortalFunctions;

  useEffect(() => {
    Promise.all([getAllowedPaymentMethods(customerId), getAvailablePaymentMethods(customerId)]);
  }, [customerId, getAllowedPaymentMethods, getAvailablePaymentMethods]);

  const availablePaymentMethodsForTheCustomer = (availablePaymentMethods[customerId] || []).sort(
    (a, _) => (a.primary ? -1 : 1)
  );

  return (
    <>
      <div className="my-3 text-gray-700 p-2 bg-gray-100 rounded-lg">
        Save your payment details for smooth subsequent transactions
      </div>
      <Modal
        title={
          <div className="flex space-x-4">
            <InfoIcon className="h-6 w-6 text-blue-600" />
            <div className="text-lg font-semibold ">Success</div>
          </div>
        }
        isOpen={paymentMethodAddedSuccessDialogueVisible.open}
        onClose={() => {
          setPaymentMethodAddedSuccessDialogueVisible({
            ...paymentMethodAddedSuccessDialogueVisible,
            open: false
          });
        }}
        hideCancelButton={true}
        onOk={() =>
          setPaymentMethodAddedSuccessDialogueVisible({
            ...paymentMethodAddedSuccessDialogueVisible,
            open: false
          })
        }
      >
        <div className="p-4">
          Thank you, we've successfully received your request to add payment method.
          <br />
          {paymentMethodAddedSuccessDialogueVisible.method === AdyenPaymentMethodType.Bacs ||
          paymentMethodAddedSuccessDialogueVisible.method === AdyenPaymentMethodType.Ach
            ? "It can take multiple days before the bank processes the request to set up a new mandate."
            : "It can take up to ten minutes before your payment method shows up."}
        </div>
      </Modal>
      <>
        <div className="flex justify-between items-center flex-wrap my-1 max-w-lg">
          <div className="font-semibold">My Payment Methods</div>
          {!addNewCardActive && (
            <button
              className="flex items-center space-x-3 text-blue-600 font-semibold hover:scale-105"
              onClick={() => setAddNewCardActive(true)}
            >
              <PlusIcon className="h-4 w-4" />
              <div>Add a New Method</div>
            </button>
          )}
        </div>
        {addNewCardActive ? (
          <div className="max-w-lg overflow-y-auto">
            <PaymentForm
              disableLoadingFirstMethodByDefault={true}
              callBacsPaymentRoute={async (data, transactionReference) => {
                if (!paymentsAdyenSessionData) {
                  throw new Error("No session data available");
                }
                return (await handleBacsPayment(transactionReference, data)).success;
              }}
              allowedPaymentMethods={
                allowedPaymentMethods[customerId]?.map(method => method.type) || []
              }
              fetchAdyenSessionForMethod={async (method: AdyenPaymentMethodType) => {
                await createAddPaymentMethodMandate({
                  customer_id: parseInt(customerId),
                  method,
                  mandated_by: AdyenMandatedBy.Client
                });
              }}
              sessionData={paymentsAdyenSessionData?.session}
              callNotifyRoute={async result => {
                if (!paymentsAdyenSessionData) {
                  console.error("No session data available");
                  return Promise.reject(
                    "Unexpected error occured, there is no paymentsSessionData"
                  );
                }
                return await sendMandateCreationNotification(
                  paymentsAdyenSessionData.transaction_data.reference,
                  result
                );
              }}
              callTransactionRoute={async (method: AdyenPaymentMethodType) => {
                if (!paymentsAdyenSessionData) {
                  console.error("No session data available");
                  return Promise.reject(
                    "Unexpected error occured, there is no paymentsSessionData"
                  );
                }
                return await createTransactionToken(
                  paymentsAdyenSessionData.transaction_data.reference,
                  parseInt(customerId),
                  method,
                  paymentsAdyenSessionData.transaction_data.expires_at
                );
              }}
              doGiactValidation={async (billingAddress, method) => {
                return await doGiactValidation({
                  customer_id: parseInt(customerId),
                  billing_address_data: billingAddress,
                  payment_method: method,
                  referrer: window.location.href
                });
              }}
              onPaymentCompleted={method => {
                setPaymentMethodAddedSuccessDialogueVisible({
                  method,
                  open: true
                });
                setAddNewCardActive(false);
              }}
              onError={error => console.error(error)}
              translations={{
                confirmPurchase: "Setup Direct Debit Mandate",
                "bacs.confirm": "Setup Direct Debit Mandate"
              }}
            />
          </div>
        ) : availablePaymentMethodsForTheCustomer.length > 0 ? (
          <div className="space-y-2 my-4 overflow-y-auto max-w-lg">
            {availablePaymentMethodsForTheCustomer.map(method => {
              const attributes = getPaymentMethodDetails(method.type);
              return (
                <div
                  className="relative rounded grid grid-cols-5 items-center border p-6 overflow-x-hidden h-fit"
                  key={method.id}
                >
                  <div className="flex space-x-4 items-center col-span-3">
                    <img src={attributes.logo} alt={method.summary} />
                    <div className="text-gray-700">{method.details}</div>
                    {method.mandated_by === AdyenMandatedBy.Self && (
                      <Popover message="Manually added on behalf of client" position="right">
                        <InfoIcon className="w-4 h-4 text-gray-500" />
                      </Popover>
                    )}
                  </div>
                  <div className="text-gray-700 text-right col-span-2">
                    {method.summary}
                    {method.primary && (
                      <div className="absolute top-2 right-[-20px] bg-blue-500 text-white text-xs font-semibold py-1 px-5 transform rotate-45 shadow-md">
                        Primary
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center my-12">
            <NoAvailablePaymentMethod />
            <div>You have not added any payment method</div>
          </div>
        )}
      </>
    </>
  );
};

export default ClientPortalCustomerManagePaymentMethods;
